import { type EagerResponse } from '../../../api/useApolloRequestWrapper'
import { usePrevious } from '../../../utils/usePrevious'
import { RETAILER_APP_ID_MAP } from '../../../constants'
import { useGetRequestContextListRequest } from '../getRequestContextList/hooks'
import { type AdminRequestContext, type Context, type RequestContext } from '../types'
import { logRequest } from './logging/logRequest'
import {
  SPROUTS_FARMERS_MARKET_RETAILER_ID,
  SPROUTS_FARMERS_MARKET_TESTING_STORE_CONFIGURATION_ID,
} from './constants'

// A Sprouts special request to filter out the testing client configuration from orders flows
// Normal this would be done by removing the partner_id from the client configuration, but
// another IPP tool uses the testing client configuration, so we need to filter it out here.
function isSproutsTestingStoreConfigurationId(
  retailerId: string,
  storeConfigurationId: string
): boolean {
  return (
    retailerId === SPROUTS_FARMERS_MARKET_RETAILER_ID &&
    storeConfigurationId === SPROUTS_FARMERS_MARKET_TESTING_STORE_CONFIGURATION_ID
  )
}

function isCompleteRequestContext(context: Partial<RequestContext>): context is RequestContext {
  return Boolean(context.clientConfigurationId && context.countryId && context.storeConfigurationId)
}

const getValidRequestContexts = (
  retailerId: string,
  getRequestContextListRequest: EagerResponse<RequestContext[]>
): RequestContext[] => {
  if (getRequestContextListRequest.type !== 'success') {
    return []
  }
  if (!getRequestContextListRequest.data) {
    return []
  }

  return getRequestContextListRequest.data
    .filter(isCompleteRequestContext)
    .filter(
      requestContext =>
        !isSproutsTestingStoreConfigurationId(
          retailerId,
          requestContext.storeConfigurationId.toString()
        )
    )
    .map(requestContext => ({
      storeConfigurationId: requestContext.storeConfigurationId,
      clientConfigurationId: requestContext.clientConfigurationId,
      countryId: requestContext.countryId,
    }))
}

export type ValidRequestContextResponse = EagerResponse<{
  context: Context | undefined
  requestContexts: RequestContext[]
}>

function getContext({
  selectedStoreConfigurationId,
  requestContexts,
  retailerAppId,
}: {
  selectedStoreConfigurationId?: string
  requestContexts: RequestContext[]
  retailerAppId: string | undefined
}): Context | undefined {
  const selectedRequestContext: RequestContext | undefined = requestContexts?.find(
    requestContext =>
      requestContext.storeConfigurationId?.toString() === selectedStoreConfigurationId
  )
  const requestContext: RequestContext | undefined = selectedRequestContext ?? requestContexts[0]
  const adminRequestContext: AdminRequestContext | undefined = retailerAppId
    ? { retailerAppId }
    : undefined

  if (!requestContext && !adminRequestContext) {
    return undefined
  }

  return { requestContext, adminRequestContext }
}

export const useGetValidRequestContexts = ({
  retailerId,
  partnerId,
  selectedStoreConfigurationId,
}: {
  retailerId: string
  partnerId: string
  selectedStoreConfigurationId?: string
}): ValidRequestContextResponse => {
  const getRequestContextListRequest = useGetRequestContextListRequest(retailerId, partnerId)
  const previousRequest = usePrevious(getRequestContextListRequest)
  const retailerAppId = RETAILER_APP_ID_MAP.get(retailerId)
  const requestContexts = getValidRequestContexts(retailerId, getRequestContextListRequest)
  const context = getContext({
    requestContexts,
    retailerAppId,
    selectedStoreConfigurationId,
  })

  if (
    previousRequest?.type !== 'pending' &&
    getRequestContextListRequest.type === 'success' &&
    context === undefined
  ) {
    logRequest({
      retailerId,
      retailerAppId,
      requestContexts,
    })
  }

  if (getRequestContextListRequest.type !== 'success') {
    return { ...getRequestContextListRequest }
  }

  return {
    ...getRequestContextListRequest,
    data: {
      context,
      requestContexts,
    },
  }
}
